import React, { useState, useEffect, useMemo } from 'react';
import { brandColors } from '../styles/clientManagementStyles';
import { SWAP_HISTORY_DAYS, SWAP_ACT_ON_COUNT, structureSwaps } from '../services/swapService';

// What a client swapped mid-workout, sorted by what the coach can do about it (see structureSwaps).
// A swap lasts one day and never changes the program, so without this a client can swap the same
// exercise every week and the coach never knows. Read-only on purpose: the coach changes the program
// in the editor below. `clientSwaps` is summariseSwaps' groups, or null when they could not be loaded.

const card = {
  backgroundColor: brandColors.mediumGray,
  border: `1px solid ${brandColors.lightGray}`,
  borderRadius: 14,
  padding: 20,
  marginBottom: 24,
};

const when = (date) => new Date(`${date}T12:00:00`).toLocaleDateString();
const list = (items) => items.map((i) => (i.count > 1 ? `${i.name} ×${i.count}` : i.name)).join(', ');
const times = (count) => (count === 1 ? 'once' : `${count} times`);

// A group the coach should look at: the whole story on a card.
const ActOnCard = ({ group }) => (
  <div
    data-testid="swap-act-on"
    style={{
      background: brandColors.darkGray,
      border: `1px solid ${group.hasDiscomfort ? brandColors.red : brandColors.gold}`,
      borderRadius: 8,
      padding: 14,
      marginBottom: 10,
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
      <p style={{ color: brandColors.white, fontWeight: 600, fontSize: 14, margin: 0 }}>{group.originalName}</p>
      <span style={{ color: group.hasDiscomfort ? brandColors.red : brandColors.gold, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
        {times(group.count)}
      </span>
    </div>
    <p style={{ color: brandColors.silver, fontSize: 13, margin: '4px 0 0' }}>→ {list(group.replacements)}</p>
    {group.reasons.length > 0 && (
      <p style={{ color: group.hasDiscomfort ? brandColors.red : brandColors.warning, fontSize: 13, margin: '4px 0 0' }}>
        {group.reasons.map((r) => (r.count > 1 ? `${r.name} ×${r.count}` : r.name)).join(' · ')}
      </p>
    )}
    <p style={{ color: brandColors.white, fontSize: 12, margin: '8px 0 0' }}>{group.advice}</p>
    <p style={{ color: brandColors.silver, fontSize: 11, margin: '4px 0 0' }}>Last on {when(group.lastDate)}</p>
  </div>
);

// Everything else: one line each, so a long tail stays short.
const CompactRow = ({ group }) => (
  <div
    data-testid="swap-compact"
    style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderTop: `1px solid ${brandColors.lightGray}` }}
  >
    <span style={{ color: brandColors.white, fontSize: 13, minWidth: 0 }}>
      {group.originalName || 'Exercise not recorded'}
      <span style={{ color: brandColors.silver }}> → {list(group.replacements)}</span>
    </span>
    <span style={{ color: brandColors.silver, fontSize: 12, flexShrink: 0 }}>
      {times(group.count)} · {when(group.lastDate)}
    </span>
  </div>
);

// A part that starts closed. The button says how many are inside.
const Fold = ({ id, title, note, groups, open, onToggle }) => {
  if (groups.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', border: 'none', padding: '4px 0', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ color: brandColors.white, fontSize: 13, fontWeight: 600 }}>
          {title} <span style={{ color: brandColors.silver, fontWeight: 400 }}>({groups.length})</span>
        </span>
        <span style={{ color: brandColors.silver, fontSize: 12 }}>{open ? 'Hide' : 'Show'}</span>
      </button>
      <p style={{ color: brandColors.silver, fontSize: 11, margin: '0 0 6px' }}>{note}</p>
      {open && groups.map((group) => <CompactRow key={group.key} group={group} />)}
    </div>
  );
};

const ClientSwapsPanel = ({ clientSwaps }) => {
  const [open, setOpen] = useState({});
  // A different client (or a reload) starts with the folds closed again
  useEffect(() => { setOpen({}); }, [clientSwaps]);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  const sorted = useMemo(() => (clientSwaps ? structureSwaps(clientSwaps) : null), [clientSwaps]);

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ color: brandColors.gold, fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase' }}>⇄ Client swaps</span>
        {sorted && sorted.actOn.length > 0 && (
          <span style={{ background: brandColors.gold, color: brandColors.darkGray, borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
            {sorted.actOn.length} to act on
          </span>
        )}
      </div>
      <p style={{ color: brandColors.silver, fontSize: 11, margin: '0 0 14px' }}>
        {sorted && sorted.swapCount > 0
          ? `${sorted.swapCount} swap${sorted.swapCount === 1 ? '' : 's'} across ${sorted.exerciseCount} exercise${sorted.exerciseCount === 1 ? '' : 's'} in the last ${SWAP_HISTORY_DAYS} days. `
          : `Exercises swapped during a workout in the last ${SWAP_HISTORY_DAYS} days. `}
        A swap lasts one day and does not change the program.
      </p>

      {sorted === null ? (
        <p style={{ color: brandColors.warning, fontSize: 13, margin: 0 }}>Could not load swaps. Refresh to try again.</p>
      ) : sorted.swapCount === 0 ? (
        <p style={{ color: brandColors.silver, fontSize: 13, margin: 0 }}>No swaps</p>
      ) : (
        <>
          {sorted.actOn.length > 0 ? (
            sorted.actOn.map((group) => <ActOnCard key={group.key} group={group} />)
          ) : (
            <p style={{ color: brandColors.silver, fontSize: 13, margin: 0 }}>Nothing stands out: no pain reported and no exercise swapped {SWAP_ACT_ON_COUNT} times or more.</p>
          )}
          <Fold
            id="equipment"
            title="The gym did not have it"
            note="Swapped because the equipment was missing or taken. Worth knowing about the client's gym."
            groups={sorted.equipment}
            open={!!open.equipment}
            onToggle={toggle}
          />
          <Fold
            id="occasional"
            title="Now and then"
            note="Swapped once or twice, for a preference or no reason."
            groups={sorted.occasional}
            open={!!open.occasional}
            onToggle={toggle}
          />
        </>
      )}
    </div>
  );
};

export default ClientSwapsPanel;
